// Milestone tab — orchestrates list + CRUD + archive for milestone
// tracking items under a contract. Milestones are non-recurring, so
// there is no Generate Occurrences workflow. Matches the other
// tracking tabs' archived-item deep-link banner, include-archived
// filter invariant, and post-create row highlight.
//
// The milestone, SOW, and variation lists are fetched with the full-drain
// hook (every page, no item cap) and gated behind a whole-tab
// loading/error boundary — every derived surface sums across all three
// lists, so partial inputs must never render as authoritative values.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Pencil, Archive as ArchiveIcon, CheckSquare } from "lucide-react";
import Alert from "../../../common/Alert";
import Button from "../../../common/Button";
import TrackingTabShell from "../shared/TrackingTabShell";
import { useDeepLinkPageJump } from "../shared/useDeepLinkPageJump";
import { useSowScopedCreateAction } from "../shared/useSowScopedCreateAction";
import TrackingTable, { type Column } from "../shared/TrackingTable";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import ArchiveDialog from "../shared/ArchiveDialog";
import MilestoneFormModal, {
  type MilestoneFormMode,
} from "./MilestoneFormModal";
import MilestoneInlineExpand from "./MilestoneInlineExpand";
import MilestoneDetailExpand from "./MilestoneDetailExpand";
import MilestoneDetailModal from "./MilestoneDetailModal";
import {
  useArchiveTrackingItem,
  useCreateTrackingItem,
  useTrackingItem,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import { useFlatTrackingList } from "../../../../hooks/useFlatTrackingList";
import LoadingSpinner from "../../../common/LoadingSpinner";
import { deriveMilestoneDisplayLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";
import StepperCell from "./StepperCell";
import ProgressMilestoneModal from "./ProgressMilestoneModal";
import PaymentCell from "./PaymentCell";
import MilestoneSummaryStrip from "./MilestoneSummaryStrip";
import { summariseSowAllocation } from "./sowAllocation";
import TableViewToggle from "./TableViewToggle";
import ProgressDotsKey from "./ProgressDotsKey";
import MilestoneGroupedTable from "./MilestoneGroupedTable";
import TimelineContextBar from "./TimelineContextBar";
import TimelineActiveWindow from "./TimelineActiveWindow";
import TimelineZoomControls from "./TimelineZoomControls";
import TimelineLegendInline from "./TimelineLegendInline";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { deriveMilestoneDisplayStatus } from "../../../../utils/deriveMilestoneDisplayStatus";
import {
  daysBetween,
  effectiveTimelineDate,
  isIsoDate,
  windowRange,
} from "./timelineGeometry";
import { smartTimelineDefault } from "./smartTimelineDefaults";
import MilestoneNumberBadge from "./MilestoneNumberBadge";
import SlipTag from "./SlipTag";
import { milestoneRowSubtitle } from "./milestoneRowSubtitle";
import MilestoneFilterChipBar from "./MilestoneFilterChipBar";
import { sortMilestones, type MilestoneSortDir } from "./sortMilestones";
import { useTableSort } from "../shared/useTableSort";
import { formatSowDisplayNumber } from "./formatSowDisplayNumber";
import MilestoneActiveFilterSummary from "./MilestoneActiveFilterSummary";
import TrackingPagination from "../shared/TrackingPagination";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { applyMilestoneFilters } from "./applyMilestoneFilters";
import {
  DEFAULT_TIMELINE_WINDOW,
  parseMilestoneUrlState,
  serialiseMilestoneUrlState,
  TIMELINE_WINDOW_STEPS,
  type MilestoneFilterChipBarFilters,
  type TimelineWindowDays,
} from "./milestoneUrlState";
import ScopeSelector from "../shared/ScopeSelector";
import { applyScopeFilter } from "../shared/applyScopeFilter";
import { ALL_SCOPE, type ScopeValue } from "../shared/scopeTypes";
import {
  contractOnlyValue,
  currentSowValue,
  totalContractValue,
} from "../variations-sows/contractValueDerivation";
import type {
  CreateMilestoneInput,
  Milestone,
  Sow,
  UpdateMilestoneInput,
  Variation,
} from "../../../../types";

interface MilestoneTabProps {
  contractId: string;
  itemIdFromUrl?: string | null;
  /** Contract's total value in AUD whole dollars (per ManagedContract.value).
   *  Threaded through from ManagedContractDetail so the per-row payment
   *  subtitle can render "· N% of contract". Undefined or zero gates the
   *  percentage off entirely (see PaymentCell empty-state rules). */
  contractValue?: number;
  /** Contract term boundaries (YYYY-MM-DD). When both are present and the
   *  milestone list is non-empty, the timeline renders above the table;
   *  otherwise the timeline is hidden. */
  contractStartDate?: string;
  contractEndDate?: string;
  /** Stored GST intent on the parent contract. Threaded through to the
   *  summary strip and used to compute the milestone-value column-header
   *  suffix ("ex GST" / "no GST" / "GST status not recorded"). */
  gstApplies?: boolean;
  /** Callback fired when a DependencyLink in the inline-expand detail
   *  view is clicked. Parent (ManagedContractDetail) wires this to
   *  setSearchParams({ tab: "deliverable", item: id }) so the user
   *  navigates to the deliverable's row in the deliverables tab. */
  onNavigateToDeliverable?: (deliverableId: string) => void;
  /** Callback fired when the SOW link in the detail-expand view is
   *  clicked. Parent wires to setSearchParams({ tab: "variations-sows",
   *  item: sowId }) so the user lands on the SOW's row. */
  onNavigateToSow?: (sowId: string) => void;
}

export default function MilestoneTab({
  contractId,
  itemIdFromUrl,
  contractValue,
  gstApplies,
  contractStartDate,
  contractEndDate,
  onNavigateToDeliverable,
  onNavigateToSow,
}: MilestoneTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseMilestoneUrlState(searchParams),
    [searchParams],
  );
  const setUrlState = (next: Parameters<typeof serialiseMilestoneUrlState>[0]) => {
    setSearchParams(serialiseMilestoneUrlState(next, searchParams), {
      replace: true,
    });
  };
  const includeArchived = urlState.filters.includeArchived;
  const search = urlState.search;
  const filters = urlState.filters;
  const page = urlState.page;
  const pageSize = urlState.pageSize;

  const tabRootRef = useRef<HTMLDivElement>(null);

  const [modalMode, setModalMode] = useState<MilestoneFormMode | null>(null);
  const [modalSource, setModalSource] = useState<Milestone | undefined>();
  // Pre-fill SOW id consumed from `?action=create&sowId=<id>` for the
  // cross-tab "Add milestone to this SOW" flow. Read once on mount,
  // then the URL params are stripped via `replace: true`.
  const [pendingCreateSowId, setPendingCreateSowId] = useState<string | null>(
    null,
  );
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [expandedMode, setExpandedMode] = useState<"view" | "edit" | null>(
    null,
  );
  const [archiveTarget, setArchiveTarget] = useState<Milestone | null>(null);
  const [progressTarget, setProgressTarget] = useState<Milestone | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [detailItem, setDetailItem] = useState<Milestone | null>(null);
  const [archiveBanner, setArchiveBanner] = useState(true);
  // Bumped by handleClearAll so the chip-bar's local search-input state
  // forcibly resyncs with the prop even when the prop value didn't
  // visibly change (mid-debounce reset race). Without this, typing then
  // immediately Clear-all'ing leaves the input populated because the
  // typed text never reached the parent's `search` state.
  const [filterResetCounter, setFilterResetCounter] = useState(0);

  type SortKey = "number" | "name" | "progress" | "value" | "status";
  // Milestone always has an active column (defaults to "number") and only
  // toggles ascending/descending — allowClear:false disables the shared
  // hook's 3-state cycle-with-clear so this stays a 2-state toggle.
  const { sortState, ariaSortFor, handleSortClick } = useTableSort<SortKey>(
    { column: "number", direction: "ascending", cleared: false },
    { allowClear: false },
  );
  const sortKey = sortState.column ?? "number";
  const sortDir: MilestoneSortDir = sortState.direction === "descending" ? "desc" : "asc";

  // Milestones are loaded contract-wide via the full-drain hook (every page,
  // not a capped first fetch) and paginated client-side.
  const milestonesQuery = useFlatTrackingList<Milestone>(
    contractId,
    "milestone",
    { includeArchived },
  );

  const items: Milestone[] = useMemo(
    () => milestonesQuery.data ?? [],
    [milestonesQuery.data],
  );

  // Pipeline order: archive (via API includeArchived) → scope → strip
  // → chip + search → paginate. Scope is an explicit user-facing slice
  // (All / Contract / SOW) so it shapes BOTH the strip counts and the
  // table.
  const scopedItems = useMemo(
    () => applyScopeFilter(items, urlState.scope),
    [items, urlState.scope],
  );

  // SOWs are loaded contract-wide so the inline expand can resolve a
  // milestone's stored `sowId` to the SOW's number/name caption.
  // includeArchived=true so a milestone pinned to a since-archived SOW
  // still resolves on read.
  const sowsQuery = useFlatTrackingList<Sow>(contractId, "sow", {
    includeArchived: true,
  });
  const sows: Sow[] = useMemo(
    () => sowsQuery.data ?? [],
    [sowsQuery.data],
  );
  // Keyed lookup so per-row rendering (the "#" column, once per row per
  // render) doesn't re-scan the SOW list — mirrors DeliverableTab's
  // milestonesById pattern.
  const sowsById = useMemo(() => {
    const map: Record<string, Sow> = {};
    for (const s of sows) map[s.id] = s;
    return map;
  }, [sows]);

  // Variations are loaded contract-wide so the top-left value tile can
  // reflect the user's active scope: All sums head + SOWs + every approved
  // variation; Contract sums head + variations targeting the contract;
  // SOW shows the SOW's current value (originalValue + targeted approved
  // variations). includeArchived=true so per-target rules still see the
  // full set; the derivation helpers themselves drop archived rows.
  const variationsQuery = useFlatTrackingList<Variation>(
    contractId,
    "variation",
    { includeArchived: true },
  );
  const variations: Variation[] = useMemo(
    () => variationsQuery.data ?? [],
    [variationsQuery.data],
  );

  // Every derived surface on this tab (value tiles, summary strip, timeline,
  // tables) sums across all three lists, so a single failed or unfinished
  // drain must degrade the WHOLE tab — partial inputs would silently
  // understate contract/SOW values above an erroring table.
  const isPending =
    milestonesQuery.isPending || sowsQuery.isPending || variationsQuery.isPending;
  const error =
    milestonesQuery.error ?? sowsQuery.error ?? variationsQuery.error;

  // Apply chip + search filters client-side. Status uses derivePillStatus
  // against today (Brisbane), so results match the table's pill column
  // exactly. archived gating is shared with the API includeArchived
  // param above — items already excludes archived rows when the filter
  // is off, so the helper's archived branch is effectively a no-op
  // there but stays defensive for future direct callers.
  const filteredItems = useMemo(() => {
    return applyMilestoneFilters(
      scopedItems,
      filters,
      search,
    );
  }, [scopedItems, filters, search]);

  // Sequential-view sort runs ahead of pagination so a sorted column reads
  // consistently across page boundaries. Grouped view ignores the sort and
  // continues to use placement + within-group ordering from milestoneGrouping.
  const sortedFilteredItems = useMemo(() => {
    if (urlState.view !== "sequential") return filteredItems;
    return sortMilestones(filteredItems, sortKey, sortDir);
  }, [filteredItems, urlState.view, sortKey, sortDir]);

  // Client-side pagination over the filtered list. The page slice is
  // recomputed on every URL state change; out-of-range pages clamp to
  // the last available page so a stale URL link doesn't render an
  // empty page. The unfiltered total is shown in the footer caption so
  // the user always sees the dataset size, not the narrowed slice.
  const totalPages = Math.max(1, Math.ceil(sortedFilteredItems.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedItems = useMemo(() => {
    const startIndex = (safePage - 1) * pageSize;
    return sortedFilteredItems.slice(startIndex, startIndex + pageSize);
  }, [sortedFilteredItems, safePage, pageSize]);


  // Deep-link page-jump: when ?item=<id> targets a milestone on a later page,
  // jump the client-side paginator to the page that holds it (otherwise it is
  // highlighted off-screen and appears not to show), and open the row's view
  // detail expand so the linked record is readable without a second click.
  const deepLinkTargetIndex = useMemo(
    () =>
      itemIdFromUrl
        ? sortedFilteredItems.findIndex((i) => i.id === itemIdFromUrl)
        : -1,
    [itemIdFromUrl, sortedFilteredItems],
  );
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: deepLinkTargetIndex,
    pageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== page) setUrlState({ ...urlState, page: targetPage });
      setHighlightRowId(itemId);
      setExpandedRowId(itemId);
      setExpandedMode("view");
    },
  });

  // `deepLinkInList` (not "in page") = the item exists anywhere in the loaded
  // contract list. When true the page-jump above reveals it, so the
  // missing-item banner stays suppressed; it fires only for genuinely-absent
  // (deleted/archived-excluded) ids.
  const deepLinkInList = itemIdFromUrl
    ? items.some((i) => i.id === itemIdFromUrl)
    : false;
  const missingItemQuery = useTrackingItem(
    contractId,
    "milestone",
    !deepLinkInList && itemIdFromUrl ? itemIdFromUrl : "",
  );
  const missingItem =
    missingItemQuery.data && missingItemQuery.data.type === "milestone"
      ? (missingItemQuery.data as Milestone)
      : null;

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();

  const handleOpenCreate = () => {
    setModalSource(undefined);
    setModalMode("create");
  };

  // Cross-tab "Add milestone to this SOW" flow: when the URL carries
  // `?action=create&scope=sow:<id>`, open the create modal with the SOW
  // pre-selected, then strip ONLY `action` so the user lands in SOW
  // view after closing.
  useSowScopedCreateAction({
    searchParams,
    setSearchParams,
    scopeSowId: urlState.scope.mode === "sow" ? urlState.scope.sowId : null,
    sowsLoaded: sowsQuery.data !== undefined,
    sows,
    onTrigger: (sowId) => {
      setPendingCreateSowId(sowId);
      setModalSource(undefined);
      setModalMode("create");
    },
  });

  // Broken-link guard for `?scope=sow:<unknown-or-archived-or-cancelled>`.
  useEffect(() => {
    const scope = urlState.scope;
    if (scope.mode !== "sow") return;
    if (sowsQuery.data === undefined) return;
    const target = sowsById[scope.sowId];
    const isUsable =
      target && !target.archivedAt && target.status !== "cancelled";
    if (!isUsable) {
      setUrlState({
        ...urlState,
        scope: ALL_SCOPE,
        page: 1,
      });
    }
    // setUrlState is omitted from deps because the effect is
    // self-terminating: it only fires when scope.mode === "sow", and
    // the fix-up flips scope to ALL_SCOPE so the next render won't
    // re-enter. setUrlState itself is rebuilt each render but that's
    // harmless here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState.scope, sowsById, sowsQuery.data]);

  const handleScopeChange = (next: ScopeValue) => {
    // Clear stale SOW chip selection when leaving All view to prevent
    // an invisible filter from surviving the switch.
    const nextSow = next.mode === "all" ? urlState.filters.sow : [];
    setUrlState({
      ...urlState,
      scope: next,
      filters: { ...urlState.filters, sow: nextSow },
      page: 1,
    });
  };

  const selectedSow = (() => {
    const scope = urlState.scope;
    if (scope.mode !== "sow") return null;
    return sowsById[scope.sowId] ?? null;
  })();

  // Scope-aware value tile inputs. The leftmost summary card title and
  // headline value flip with the active scope:
  //   - All:      head + non-cancelled SOW originals + every approved
  //               non-archived variation impact (totalContractValue).
  //   - Contract: head + approved variations whose targets contain the
  //               head contract, applying the per-target three-case rule.
  //   - SOW:      sow.originalValue + approved variations targeting that
  //               SOW (currentSowValue).
  // contractValue may be undefined while the parent contract query is
  // still in flight; we fall back to 0 then so the strip's pct denominator
  // doesn't NaN.
  // Current total contract value: head + active/completed SOWs +
  // approved variations. Used as the denominator for the per-row
  // "Milestone value" percentage so the percentage tracks the
  // committed contract size, not the seed value entered at creation.
  // The detail-page contract record only carries the initial `value`,
  // so we derive the current total here from the same data the strip
  // already consumes.
  const currentContractTotal = useMemo(
    () =>
      totalContractValue(
        { value: contractValue ?? 0 },
        sows,
        variations,
      ).total,
    [contractValue, sows, variations],
  );

  // Milestone-total vs SOW-value reconciliation for the value tile —
  // only meaningful when a single SOW is in scope. Uses scopedItems
  // (scope-filtered, before status/search filters) and drops archived
  // milestones explicitly, so neither the status chips nor the
  // Include-archived toggle can skew the reconciliation — matching
  // currentSowValue, which likewise ignores archived variations.
  const sowAllocation = useMemo(() => {
    if (urlState.scope.mode !== "sow" || !selectedSow) return undefined;
    return summariseSowAllocation(
      scopedItems.filter((m) => !m.archivedAt),
      currentSowValue(selectedSow, variations),
    );
  }, [urlState.scope.mode, selectedSow, scopedItems, variations]);

  const { scopedTileValue, scopedTileLabel } = useMemo(() => {
    const scope = urlState.scope;
    const head = contractValue ?? 0;
    if (scope.mode === "sow") {
      if (!selectedSow) {
        return { scopedTileValue: 0, scopedTileLabel: "SOW value" };
      }
      return {
        scopedTileValue: currentSowValue(selectedSow, variations),
        scopedTileLabel: "SOW value",
      };
    }
    if (scope.mode === "contract") {
      return {
        scopedTileValue: contractOnlyValue({ value: head }, variations),
        scopedTileLabel: "Contract value",
      };
    }
    return {
      scopedTileValue: totalContractValue({ value: head }, sows, variations)
        .total,
      scopedTileLabel: "Total contract value",
    };
  }, [urlState.scope, contractValue, selectedSow, sows, variations]);
  const handleCloseModal = () => {
    setModalMode(null);
    setPendingCreateSowId(null);
  };

  const handleCreate = async (input: CreateMilestoneInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "milestone",
      input,
    })) as Milestone | undefined;
    if (created?.id) setHighlightRowId(created.id);
  };

  const handleInlineUpdate = (item: Milestone) => async (
    input: UpdateMilestoneInput,
  ) => {
    await updateMut.mutateAsync({
      contractId,
      type: "milestone",
      itemId: item.id,
      input,
    });
    setExpandedMode("view");
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: "milestone",
      itemId: archiveTarget.id,
      reason,
    });
    setArchiveTarget(null);
    // Archiving from the inline expand also collapses it — there's no
    // longer a row to expand once the row has gone.
    if (expandedRowId === archiveTarget.id) {
      setExpandedRowId(null);
      setExpandedMode(null);
    }
  };

  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const restoreRowFocus = (id: string) => {
    rowRefs.current[id]?.focus();
  };

  const openExpand = (item: Milestone, mode: "view" | "edit") => {
    setExpandedRowId((prev) => {
      if (prev === item.id && expandedMode === mode) {
        setExpandedMode(null);
        return null;
      }
      setExpandedMode(mode);
      return item.id;
    });
  };

  const todayForRows = getBrisbaneToday();

  // Sort props are attached only in Sequential view; Grouped view uses
  // milestoneGrouping.placeMilestone + sortWithinGroup, so sortable headers
  // would mislead the user there.
  const sortProp = (key: SortKey) =>
    urlState.view === "sequential"
      ? {
          ariaSort: ariaSortFor(key),
          onSortClick: () => handleSortClick(key),
        }
      : undefined;

  const columns: Column<Milestone>[] = [
    {
      header: "#",
      cell: (i) => {
        const sow = i.sowId ? sowsById[i.sowId] : undefined;
        const sowDisplay = sow ? formatSowDisplayNumber(sow.number) : null;
        return (
          <div className="flex flex-col items-start gap-0.5">
            {sow && (
              <span
                data-testid={`row-sow-prefix-${i.id}`}
                title={`Belongs to ${sowDisplay} · ${sow.name}`}
                className="max-w-full truncate font-mono text-caption text-slate-500 dark:text-slate-400"
              >
                {sowDisplay}
              </span>
            )}
            <MilestoneNumberBadge
              milestoneNumber={i.milestoneNumber}
              milestoneSuffix={i.milestoneSuffix}
              urgencyStatus={deriveMilestoneDisplayLifecycle(i)}
            />
          </div>
        );
      },
      width: "w-24",
      sort: sortProp("number"),
    },
    {
      header: "Name",
      cell: (i) => {
        const subtitle = milestoneRowSubtitle(i, todayForRows);
        return (
          <div>
            <div>{i.name}</div>
            {subtitle.text && (
              <div
                data-testid={`row-subtitle-${i.id}`}
                className="mt-0.5 flex flex-wrap items-center gap-2 text-caption text-slate-500 dark:text-slate-400"
              >
                <span>{subtitle.text}</span>
                {subtitle.slipTag && (
                  <SlipTag
                    variant={subtitle.slipTag.variant}
                    days={subtitle.slipTag.days}
                  />
                )}
              </div>
            )}
          </div>
        );
      },
      clickable: true,
      width: "w-[35%]",
      sort: sortProp("name"),
    },
    {
      header: "Progress",
      cell: (i) => {
        const canProgress =
          !i.archivedAt && deriveMilestoneDisplayLifecycle(i) !== "paid";
        return (
          <div className="flex items-center gap-2">
            <StepperCell milestone={i} />
            {canProgress && (
              <button
                type="button"
                onClick={(e) => {
                  // Stop propagation so the row's onRowClick doesn't
                  // also fire (which would open the read-only detail
                  // expand and steal focus from the modal).
                  e.stopPropagation();
                  setProgressTarget(i);
                }}
                aria-label={`Update ${i.name} status`}
                title="Update status"
                data-testid={`progress-trigger-${i.id}`}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-brand-300"
              >
                <CheckSquare className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      },
      width: "w-32",
      sort: sortProp("progress"),
    },
    {
      header: `Milestone value (${gstSuffix(gstApplies)})`,
      align: "right",
      cell: (i) => (
        <PaymentCell
          amount={i.paymentAmount}
          contractValue={currentContractTotal}
        />
      ),
      width: "w-[18%]",
      sort: sortProp("value"),
    },
    {
      header: "Status",
      cell: (i) => {
        const display = deriveMilestoneDisplayStatus(i);
        const lifecycle = deriveMilestoneDisplayLifecycle(i);
        const isPartialPaid =
          lifecycle === "partially-accepted" &&
          Boolean(i.paidAt);
        return (
          <TrackingStatusBadge
            kind="milestone"
            status={{
              ...display,
              lifecycle,
              ...(isPartialPaid ? { secondaryPill: "paid" as const } : {}),
            }}
          />
        );
      },
      width: "w-[15%]",
      sort: sortProp("status"),
    },
    {
      header: "Actions",
      align: "right",
      cell: (i) => (
        <div className="inline-flex gap-1">
          <Button
            variant="secondary"
            iconOnly
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            aria-label={`Edit ${i.name}`}
            title={`Edit ${i.name}`}
            onClick={(e) => {
              e.stopPropagation();
              rowRefs.current[i.id] = e.currentTarget;
              openExpand(i, "edit");
            }}
          />
          {!i.archivedAt && (
            <Button
              variant="secondary"
              iconOnly
              size="sm"
              icon={<ArchiveIcon className="h-4 w-4" />}
              aria-label={`Archive ${i.name}`}
              title={`Archive ${i.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setArchiveTarget(i);
              }}
            />
          )}
        </div>
      ),
      width: "w-20",
    },
  ];

  const handleClearAll = () => {
    setUrlState({
      ...urlState,
      filters: {
        status: [],
        awaitingAcceptance: false,
        sow: [],
        includeArchived: false,
      },
      search: "",
      page: 1,
    });
    setFilterResetCounter((c) => c + 1);
  };

  const handlePageChange = (next: number) => {
    setUrlState({ ...urlState, page: next });
  };

  const handlePageSizeChange = (next: number) => {
    setUrlState({ ...urlState, pageSize: next, page: 1 });
  };

  // Whole-tab degraded-state boundary (rendering only — every hook above runs
  // unconditionally). Until all three drains settle, no derived surface may
  // render: a value tile computed without the variations, or a summary strip
  // missing SOWs, reads as an authoritative (wrong) number.
  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="error">
        {error instanceof Error ? error.message : "Failed to load tracking items."}
      </Alert>
    );
  }

  return (
    <div className="space-y-4" ref={tabRootRef}>
      {itemIdFromUrl && !deepLinkInList && missingItem && archiveBanner && (
        <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {missingItem.archivedAt
                ? `This milestone was archived on ${formatDayMonthYear(
                    missingItem.archivedAt.slice(0, 10),
                  )}.`
                : `This milestone is not shown in the current list.`}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => setDetailItem(missingItem)}
            >
              View archived item
            </button>
          </div>
        </Alert>
      )}

      {itemIdFromUrl &&
        !deepLinkInList &&
        missingItemQuery.error &&
        archiveBanner && (
          <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
            Item not found. It may have been deleted.
          </Alert>
        )}

      <ScopeSelector
        scope={urlState.scope}
        onScopeChange={handleScopeChange}
        sows={sows}
        itemNoun="milestone"
      />

      <MilestoneSummaryStrip
        milestones={scopedItems}
        contractValue={scopedTileValue}
        gstApplies={gstApplies}
        valueLabel={scopedTileLabel}
        sowAllocation={sowAllocation}
      />

      {(() => {
        // Compute timeline range against the active scope.
        // - All / Contract: contract term (existing behaviour)
        // - SOW: the selected SOW's term, with a title/subtitle that
        //   matches the rescoped surface so the heading isn't misleading.
        const isSow = urlState.scope.mode === "sow" && selectedSow;
        const timelineStart = isSow
          ? selectedSow.startDate
          : contractStartDate;
        const timelineEnd = isSow ? selectedSow.endDate : contractEndDate;
        if (!timelineStart || !timelineEnd) return null;
        if (scopedItems.length === 0) return null;

        const today = getBrisbaneToday();
        // Smart default applies only when the user hasn't explicitly chosen
        // a zoom or focus via the URL. Once they touch the +/- controls or
        // scrub the context bar, both `window` and `focus` enter the URL
        // and `urlState` carries the explicit choice forward — that's the
        // signal to stop overriding.
        const hasExplicitTimelineParams =
          searchParams.has("window") || searchParams.has("focus");
        const smart =
          !hasExplicitTimelineParams
            ? smartTimelineDefault(scopedItems, timelineStart, timelineEnd)
            : null;
        const focusInBounds =
          isIsoDate(urlState.timelineFocus) &&
          urlState.timelineFocus! >= timelineStart &&
          urlState.timelineFocus! <= timelineEnd;
        const focus = smart?.focus ?? (focusInBounds ? urlState.timelineFocus! : today);
        const effectiveWindow = smart?.window ?? urlState.timelineWindow;
        const { start: windowStart, end: windowEnd } = windowRange(
          focus,
          effectiveWindow,
          timelineStart,
          timelineEnd,
        );

        const inWindowCount = scopedItems.filter((m) => {
          const iso = effectiveTimelineDate(m);
          return !!iso && iso >= windowStart && iso <= windowEnd;
        }).length;

        const futureMilestones = scopedItems
          .map((m) => m.plannedDate)
          .filter((d): d is string => typeof d === "string" && d >= today)
          .sort();
        const daysUntilNext =
          futureMilestones.length > 0
            ? Math.max(0, daysBetween(today, futureMilestones[0]))
            : undefined;

        // Whenever the user takes an explicit timeline action (zoom, scrub,
        // widen) we capture BOTH dimensions of the smart default into URL
        // state, then apply the user's change on top. Without this, the
        // first interaction silently drops whichever smart-default value the
        // user wasn't touching — e.g. scrubbing focus would revert the
        // smart-chosen 30-day window back to the 90-day URL default,
        // because writing `?focus=...` flips hasExplicitTimelineParams=true
        // and the unwritten smart window evaporates on the next render.
        const captureSmartDefaults = (overrides: {
          timelineWindow?: TimelineWindowDays;
          timelineFocus?: string | null;
        }) => {
          setUrlState({
            ...urlState,
            timelineWindow: overrides.timelineWindow ?? effectiveWindow,
            timelineFocus:
              overrides.timelineFocus !== undefined
                ? overrides.timelineFocus
                : focus,
          });
        };

        const handleWindowChange = (next: TimelineWindowDays) => {
          captureSmartDefaults({ timelineWindow: next });
        };

        const handleShowWiderWindow = () => {
          // Promote to the smallest zoom step that brings at least one
          // milestone into view. Compare against the resolved effective
          // window (which may be the smart default) rather than the bare
          // urlState value, so the empty-window CTA respects whatever
          // zoom the user is currently looking at.
          for (const step of TIMELINE_WINDOW_STEPS) {
            if (step <= effectiveWindow) continue;
            const range = windowRange(focus, step, timelineStart, timelineEnd);
            const visible = scopedItems.some((m) => {
              const iso = effectiveTimelineDate(m);
              return !!iso && iso >= range.start && iso <= range.end;
            });
            if (visible) {
              captureSmartDefaults({ timelineWindow: step });
              return;
            }
          }
          captureSmartDefaults({ timelineWindow: 180 });
        };

        const handleActivate = (id: string) => {
          const target = items.find((m) => m.id === id);
          if (target) {
            setHighlightRowId(id);
            openExpand(target, "view");
          }
        };

        return (
          <section
            data-testid="milestone-timeline"
            aria-label={isSow ? "SOW timeline" : "Contract milestone timeline"}
            className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
          >
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">
                  {isSow ? "SOW timeline" : "Contract timeline"}
                </h3>
                <p className="text-caption text-slate-500 dark:text-slate-400">
                  Payment milestones plotted against {isSow ? "SOW" : "contract"} term · {inWindowCount} in active window
                </p>
              </div>
              <TimelineZoomControls
                value={effectiveWindow}
                onChange={handleWindowChange}
                onReset={() =>
                  setUrlState({
                    ...urlState,
                    timelineWindow: DEFAULT_TIMELINE_WINDOW,
                    timelineFocus: null,
                  })
                }
                canReset={hasExplicitTimelineParams}
              />
            </header>
            <TimelineContextBar
              contractStart={timelineStart}
              contractEnd={timelineEnd}
              today={today}
              milestones={scopedItems}
              windowStart={windowStart}
              windowEnd={windowEnd}
              onWindowScrub={(centreIso) =>
                captureSmartDefaults({ timelineFocus: centreIso })
              }
            />
            <div
              data-testid="timeline-section-divider"
              className="border-t border-dashed border-slate-200 pt-2 dark:border-slate-700"
              aria-hidden="true"
            />
            <TimelineActiveWindow
              milestones={scopedItems}
              windowStart={windowStart}
              windowEnd={windowEnd}
              today={today}
              daysUntilNext={daysUntilNext}
              onShowWiderWindow={handleShowWiderWindow}
              onActivate={handleActivate}
            />
            <TimelineLegendInline />
          </section>
        );
      })()}

      {items.length > 0 && (
        <>
          <MilestoneActiveFilterSummary
            filters={filters}
            search={search}
            onClearAll={handleClearAll}
            sows={sows}
          />
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <MilestoneFilterChipBar
                filters={filters}
                onFiltersChange={(next: MilestoneFilterChipBarFilters) => {
                  setUrlState({ ...urlState, filters: next, page: 1 });
                }}
                search={search}
                onSearchChange={(next: string) => {
                  setUrlState({ ...urlState, search: next, page: 1 });
                }}
                sows={sows}
                showSowChip={urlState.scope.mode === "all"}
                resetCounter={filterResetCounter}
              />
            </div>
            <TableViewToggle
              value={urlState.view}
              onChange={(next) => setUrlState({ ...urlState, view: next, page: 1 })}
            />
          </div>
          <div className="flex justify-end">
            <ProgressDotsKey />
          </div>
        </>
      )}

      <TrackingTabShell
        type="milestone"
        onAdd={handleOpenCreate}
        includeArchived={includeArchived}
        onToggleArchived={(v) => {
          setUrlState({
            ...urlState,
            filters: { ...filters, includeArchived: v },
            page: 1,
          });
        }}
        isEmpty={!isPending && !error && filteredItems.length === 0}
        showIncludeArchived={false}
        emptyState={
          items.length > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-800">
              <p className="text-body text-slate-500 dark:text-slate-400">
                No milestones match your filters.
              </p>
            </div>
          ) : undefined
        }
      >
        {urlState.view === "grouped" ? (
          // Grouped view buckets the FULL filtered list — pagination is a
          // sequential-view concern only. A page slice fed in here made a
          // group's contents depend on the page size (25/page showed 3 of 13
          // needs-attention milestones); collapsible groups are the grouped
          // view's own volume control.
          <MilestoneGroupedTable
            items={filteredItems}
            today={getBrisbaneToday()}
            columns={columns}
            isLoading={isPending}
            error={error}
            rowKey={(i) => i.id}
            expandedRowId={expandedRowId}
            highlightRowId={highlightRowId}
            onRowClick={(i) => openExpand(i, "view")}
            renderExpandedRow={(item) =>
              expandedMode === "edit" ? (
                <MilestoneInlineExpand
                  item={item}
                  contractId={contractId}
                  sows={sows}
                  milestones={items}
                  onUpdate={handleInlineUpdate(item)}
                  onCancel={() => setExpandedMode("view")}
                  onArchive={() => setArchiveTarget(item)}
                  onClosed={() => restoreRowFocus(item.id)}
                  isSubmitting={updateMut.isPending}
                />
              ) : (
                <MilestoneDetailExpand
                  item={item}
                  sows={sows}
                  onEdit={
                    item.archivedAt ? undefined : () => setExpandedMode("edit")
                  }
                  onNavigateToDeliverable={onNavigateToDeliverable}
                  onNavigateToSow={onNavigateToSow}
                />
              )
            }
          />
        ) : (
          <>
            <TrackingTable<Milestone>
              columns={columns}
              items={pagedItems}
              isLoading={isPending}
              error={error}
              emptyMessage="No milestones recorded yet."
              rowKey={(i) => i.id}
              expandedRowId={expandedRowId}
              highlightRowId={highlightRowId}
              onRowClick={(i) => openExpand(i, "view")}
              tableLayout="fixed"
              renderExpandedRow={(item) =>
                expandedMode === "edit" ? (
                  <MilestoneInlineExpand
                    item={item}
                    contractId={contractId}
                    sows={sows}
                    milestones={items}
                    onUpdate={handleInlineUpdate(item)}
                    onCancel={() => setExpandedMode("view")}
                    onArchive={() => setArchiveTarget(item)}
                    onClosed={() => restoreRowFocus(item.id)}
                    isSubmitting={updateMut.isPending}
                  />
                ) : (
                  <MilestoneDetailExpand
                    item={item}
                    sows={sows}
                    onEdit={
                      item.archivedAt ? undefined : () => setExpandedMode("edit")
                    }
                    onNavigateToDeliverable={onNavigateToDeliverable}
                    onNavigateToSow={onNavigateToSow}
                  />
                )
              }
            />
            {items.length > 0 && (
              <TrackingPagination
                page={safePage}
                pageSize={pageSize}
                total={filteredItems.length}
                noun="milestone"
                testIdPrefix="milestone"
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </>
        )}
      </TrackingTabShell>

      <MilestoneFormModal
        isOpen={modalMode === "create"}
        onClose={handleCloseModal}
        mode="create"
        contractId={contractId}
        sows={sows}
        initialSowId={pendingCreateSowId ?? undefined}
        onCreate={handleCreate}
        isSubmitting={createMut.isPending}
        existingMilestones={items}
      />
      <MilestoneFormModal
        isOpen={modalMode === "edit"}
        onClose={handleCloseModal}
        mode="edit"
        contractId={contractId}
        source={modalSource}
        sows={sows}
        onUpdate={async (input) => {
          if (!modalSource) return;
          await updateMut.mutateAsync({
            contractId,
            type: "milestone",
            itemId: modalSource.id,
            input,
          });
        }}
        isSubmitting={updateMut.isPending}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget
            ? `milestone "${archiveTarget.name}"`
            : "this milestone"
        }
        isSubmitting={archiveMut.isPending}
      />

      <MilestoneDetailModal
        isOpen={!!detailItem}
        onClose={() => setDetailItem(null)}
        item={detailItem}
      />

      <ProgressMilestoneModal
        isOpen={!!progressTarget}
        milestone={progressTarget}
        onClose={() => setProgressTarget(null)}
        onProgress={async (m, update) => {
          await updateMut.mutateAsync({
            contractId,
            type: "milestone",
            itemId: m.id,
            input: { ...update, version: m.version },
          });
        }}
      />
    </div>
  );
}
